import React from "react";
import {
    useSubmit,
    useNavigation,
    useActionData,
    useSearchParams,
} from "react-router";
import { Card, Button, Form, FloatingLabel } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ResetPasswordSchema } from "schema/account";
import { renderSavingProgressToast } from "components/shared/Toasts";
import axios from "axios";

export async function action({ request }) {
    const data = await request.json();
    const url = new URL(request.url);
    const token = url.searchParams.get("token");
    // Prepare HTTP Basic Auth header
    const basicAuth = btoa(`_:${data.password}`);
    var result = await renderSavingProgressToast(
        axios.post(
            "account/reset-password",
            {}, // No body needed for basic auth
            {
                headers: {
                    Authorization: `Basic ${basicAuth}`,
                },
                params: {
                    token,
                },
            }
        )
    )
        .then(() => ({ success: true }))
        .catch((error) => {
            if (error.response?.data) {
                var data = error.response?.data;
                return Promise.resolve({
                    success: false,
                    reason: data["description"],
                });
            }
            return Promise.reject(error);
        });
    if (result.success === true) {
        window.location.replace("/");
        return null;
    }
    return result;
}

const ResetPassword = () => {
    const submit = useSubmit();
    const actionData = useActionData();
    const navigation = useNavigation();
    const isNavigating =
        navigation.state === "submitting" || navigation.state === "loading";
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const onSubmit = async (data) => {
        if (!token) {
            console.error("Missing reset token");
            return;
        }

        await submit(data, {
            action: `/account/reset-password?token=${encodeURIComponent(
                token
            )}`,
            method: "post",
            encType: "application/json",
        });
    };

    const onError = (errors, e) => console.error(errors, e);

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(ResetPasswordSchema),
        defaultValues: {
            password: "",
            confirm_password: "",
        },
    });

    return (
        <div style={{ width: "100%", maxWidth: "350px", margin: "0 auto" }}>
            {!token && <div className="alert alert-warning" role="alert"></div>}
            <Card className="mt-3" style={{ width: "100%" }} border="secondary">
                <Card.Header>Reset Password</Card.Header>
                <Card.Body>
                    <Form
                        onSubmit={handleSubmit(onSubmit, onError)}
                        onReset={reset}
                    >
                        <Form.Group className="mb-3">
                            <Controller
                                control={control}
                                name="password"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="password"
                                        label="Password"
                                    >
                                        <Form.Control
                                            type="password"
                                            name="password"
                                            isInvalid={errors.password}
                                            placeholder="Password"
                                            onChange={onChange}
                                            value={value}
                                            ref={ref}
                                            rules={{ required: true }}
                                            data-testid="input_password"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.password?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Controller
                                control={control}
                                name="confirm_password"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="confirm_password"
                                        label="Confirm Password"
                                    >
                                        <Form.Control
                                            type="password"
                                            name="confirm_password"
                                            isInvalid={errors.confirm_password}
                                            placeholder="Enter password again"
                                            onChange={onChange}
                                            value={value}
                                            ref={ref}
                                            rules={{ required: true }}
                                            data-testid="input_confirm_password"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.confirm_password?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                        {actionData?.reason && (
                            <div className="alert alert-danger" role="alert">
                                {actionData.reason}
                            </div>
                        )}
                        <div className="d-grid gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isNavigating}
                            >
                                Reset Password
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default ResetPassword;
